import { equals, indexBy, last, merge, pluck, reduce, values } from "rambda";
import { unstable_batchedUpdates } from "react-dom";
import type { ListKey } from "./monday-item.types";
import { ObserverOrCallback } from "utils/observer";
import { MondayErrorCodes } from "shared/integrations/monday-types";

// --- Types and Interfaces ---

export type MondayItemErrorInfo = {
  code: MondayErrorCodes;
  message: string;
  url?: string;
};

type Item<T = any> = { data?: T; error?: MondayItemErrorInfo };

type ItemsPage = {
  items: any[];
  cursor?: string;
};

interface OptimisticPatch {
  columnId: string;
  columnValue: any;
  columnType: string;
  timestamp: number;
  state: "pending" | "ok" | "fail";
}

interface IMondayItemRepository {
  fetchItem(id: string): Promise<any>;
  updateItem(itemId: string, patch: any, boardId?: string): Promise<any>;
}

interface IMondayLiveListsRepository {
  fetchLiveList(key: ListKey): Promise<ItemsPage>;
  fetchLiveListNextPage(key: ListKey, cursor: string): Promise<ItemsPage>;
}

// --- Helper Functions ---

const indexById = indexBy("id");

function sendToObserver<T = any>(item: Item<T>, obs: ObserverOrCallback<T>) {
  if (typeof obs === "function") {
    // function subscribers get only data
    if (item.data) obs(item.data);
  } else {
    // object subscribers can get data/error/complete signals
    if (item.data && !item.error) obs.next(item.data);
    else if (item.error) {
      // Convert MondayItemErrorInfo to Error for the observer interface
      const error = new Error(item.error.code) as Error & { code: MondayErrorCodes; url?: string };
      error.code = item.error.code;
      if (item.error.url) {
        error.url = item.error.url;
      }
      obs.error(error);
    } else obs.complete();
  }
}
const sender = (item: Item) => (obs: ObserverOrCallback<typeof item.data>) => sendToObserver(item, obs);

// functions for working with patches
// Note about inconsistency:
// monday items have columnValues as array, which is foolish since order isn't important and every column has
// a unique id anyways - a map or object is better.
// But I won't change the data layout yet, so I support columnValues array and object
function patchItemInPlace(item: { name?: string; columnValues?: any[] }, patch: OptimisticPatch) {
  if (!item) return item;
  if (patch.state == "fail") return item;
  if (patch.columnId === "name") {
    item.name = patch.columnValue;
  } else {
    item.columnValues ??= [];
    const i = item.columnValues.findIndex((c: any) => c.id === patch.columnId);
    if (i === -1) {
      item.columnValues.push({ id: patch.columnId, value: patch.columnValue, type: patch.columnType });
    } else {
      item.columnValues[i].value = patch.columnValue;
    }
  }
}

const patchReducer = reduce((item: { name?: string; columnValues: Record<string, any> }, patch: OptimisticPatch) => {
  if (patch.state === "fail") return item;
  if (patch.columnId === "name") {
    item.name = patch.columnValue;
  } else {
    item.columnValues[patch.columnId] = { id: patch.columnId, value: patch.columnValue, type: patch.columnType };
  }
  return item;
});

function combineServerItemAndLocalUpdates(serverItem: any, updates: OptimisticPatch[] | undefined) {
  if (!serverItem || !updates) {
    return serverItem;
  }
  let item = { ...serverItem, columnValues: indexById(serverItem.columnValues || []) };
  let updatedItem = patchReducer(item, updates);
  return { ...updatedItem, columnValues: values(updatedItem.columnValues) };
}

// --- Class Definition ---

//todo: create functions for getting/subscribing to items in bulk
export class MondayItemManager {
  private items = new Map<string, Item>();
  private optimisticPatches = new Map<string, OptimisticPatch[]>();
  private subscribers = new Map<string, Set<ObserverOrCallback<Item>>>();

  private liveLists = new Map<string, ItemsPage[]>();
  private liveListsRequests: Record<string, Promise<any>> = {};
  private liveListSubscribers = new Map<string, Set<ObserverOrCallback<{ itemIds: string[]; hasMore: boolean }>>>();
  private anyItemChangeSubscribers = new Set<(itemId: string, item: any) => void>();

  constructor(readonly itemRepo: IMondayItemRepository, readonly listRepo: IMondayLiveListsRepository) {}

  // --- Single items loading, updating and subscribing ---

  unsafe_set(id: string, item: any) {
    if (!id || !item) return;
    this.setItemAndNotify(id, item);
  }

  private internalGet(key: string): Item | undefined {
    const base = this.items.get(key);
    if (base?.data) {
      const patches = this.optimisticPatches.get(key);
      const data = combineServerItemAndLocalUpdates(base?.data, patches);
      return { data, error: base?.error };
    } else {
      return base; // base is either undefined or contains error
    }
  }

  getSingleItem(id: string | undefined): Item | undefined {
    if (!id) return undefined;
    return this.internalGet(id);
  }

  // Single item subscription
  subscribe(id: string, observer: ObserverOrCallback) {
    if (!id) return;

    const wasEmpty = !this.subscribers.has(id);

    // Add subscriber
    if (wasEmpty) {
      this.subscribers.set(id, new Set());
    }
    this.subscribers.get(id)!.add(observer);

    // Start background sync if first subscriber
    if (wasEmpty && !this.items.has(id)) {
      this.itemRepo
        .fetchItem(id)
        .then((value) => {
          this.setItemAndNotify(id, value);
        })
        .catch((error) => {
          this.setItemAndNotify(id, undefined, error);
        });
    }

    return () => {
      this.subscribers.get(id)?.delete(observer);
      if (this.subscribers.get(id)?.size === 0) {
        this.subscribers.delete(id);
        // todo Stop background sync if no more subscribers
      }
    };
  }

  // Setting an item can set data or error, by these rules
  // - you can pass either data with error=undefined, or data=undefined and error
  // if error: keep old data in cache, set error -> notify (don't recurse into subitems)
  // if data: place new data in cache, reset existing errors, recurse into subitems -> notify
  private setItemAndNotify(key: string, item: any, error?: Error | MondayItemErrorInfo) {
    const current = this.internalGet(key);
    if (error != undefined) {
      const errorInfo: MondayItemErrorInfo =
        error instanceof Error
          ? {
              code: Object.values(MondayErrorCodes).includes(error.message as MondayErrorCodes)
                ? (error.message as MondayErrorCodes)
                : MondayErrorCodes.UNKNOWN_ERROR,
              message: error.message,
              url: (error as any).url,
            }
          : error;
      this.items.set(key, { data: current?.data, error: errorInfo });
      this.scheduleNotifications(key);
    } else if (!equals(current?.data, item)) {
      this.items.set(key, { data: item });
      this.scheduleNotifications(key);
      if (item.subitems) {
        for (const subitem of item.subitems) {
          this.setItemAndNotify(subitem.id, subitem);
        }
      }
    }
  }

  private notifyItemSubscribers(key: string) {
    if (!key) return;

    const item = this.internalGet(key)!;
    if (!item) return;

    const subscribers = this.subscribers.get(key);
    if (subscribers) {
      const send = sender(item);
      subscribers.forEach(send);
    }

    for (const cb of this.anyItemChangeSubscribers.keys()) {
      cb(key, item);
    }
  }

  async updateItemFromPusher(id: string, update: { name?: string; columnValues?: Array<Record<string, any>> }) {
    const currentItem = this.items.get(id)?.data;
    // we get pusher events for all items in integrated board, including ones we don't have
    // in the cache. This is quite normal, so we just check it and return
    if (!currentItem || (!update.name && !update.columnValues)) {
      return;
    }
    const updated = { ...currentItem };
    if (update.name) {
      updated.name = update.name;
    }
    if (update.columnValues) {
      // columnValues are arrays. It's much easier to merge them as objects instead.
      updated.columnValues = values(merge(indexById(currentItem.columnValues), indexById(update.columnValues)));
    }
    this.setItemAndNotify(id, updated);
  }

  async updateItem(
    itemId: string,
    columnId: string,
    columnValue: any,
    columnDefinition: { id: string; type: string },
    boardId?: string
  ): Promise<void> {
    if (!itemId) return;
    if (!this.items.has(itemId)) {
      console.error("Can't update an item I don't have in cache");
      return;
    }

    // 1. Apply optimistic patch immediately
    // Get list of existing patches (create a new list if needed)
    let patches = this.optimisticPatches.get(itemId);
    if (!patches) {
      patches = [];
      this.optimisticPatches.set(itemId, patches);
    } else {
      // check that the new update actually changes something.
      // we combine existing patches together and check the new update is different than the result
      // we can also just go over the existing patches (in reverse) until we find the columnId and check just its value.
      let { name, columnValues } = combineServerItemAndLocalUpdates({ columnValues: {} }, patches);
      if (columnId === "name" && columnValue === name) {
        return;
      }
      if (columnValues.find((col: any) => col.id == columnId) == columnValue) {
        return;
      }
    }

    const patch: OptimisticPatch = {
      columnId,
      columnValue,
      columnType: columnDefinition.type,
      timestamp: Date.now(),
      state: "pending",
    };

    patches.push(patch);
    // That's it - we have the optimistic update in the system

    // 2. Now schedule a notification for UI to show the change
    this.scheduleNotifications(itemId);

    try {
      // 3. Send to server
      const _serverData = await this.itemRepo.updateItem(itemId, { [columnId]: columnValue }, boardId);
      // The server response should be the new state of the item (whether we succeeded or failed).
      // Currently we don't ask for that - so we assume success and merge the update into the item.
      // on error, an exception is thrown and the catch block will rollback.
      patch.state = "ok";
      const item = this.items.get(itemId);
      if (!item) {
        console.error("Item was lost");
        return;
      }
      // combine patch into the complete item state and remove from patches list
      patchItemInPlace(item.data, patch);
      const i = patches.indexOf(patch);
      patches.splice(i, 1);
      if (patches.length === 0) {
        this.optimisticPatches.delete(itemId);
      }
      this.scheduleNotifications(itemId);
      //todo notify lists to revalidate from server
    } catch (error) {
      // Rollback by removing the patch
      patch.state = "fail";
      const i = patches.indexOf(patch);
      patches.splice(i, 1);
      if (patches.length === 0) {
        this.optimisticPatches.delete(itemId);
      }
      this.notifyItemSubscribers(itemId);
      throw error;
    }
  }

  // --- Lists of items ---

  // Lists of items are used by synced-card-stack.
  // A request for items is defined by board-id and filters (that is the cache key)
  // Synced card stacks also subscribe to changes in any item from their board, so they
  // can check match with filters on any change.
  // Any change to items in board should revalidate filtered lists (only lists with filters !)

  // A list is made of "pages", each contains N items and possibly a cursor, used to fetch the next page.
  // Note: there is no way to update the contents of a list of items (you can't add or remove items, only
  // change the filters)

  *allItems() {
    for (const key of this.items.keys()) {
      yield this.internalGet(key);
    }
  }
  fetchLiveListAndSubscribe(listKey: ListKey, observer: ObserverOrCallback<{ itemIds: string[]; hasMore: boolean }>) {
    const key = listKey.cacheKey();
    if (!key) return;

    // Add subscriber
    const wasEmpty = !this.liveListSubscribers.has(key);
    if (wasEmpty) {
      this.liveListSubscribers.set(key, new Set());
    }
    this.liveListSubscribers.get(key)!.add(observer);

    // if we have data, trigger onData now
    const existingData = this.liveLists.get(key);
    if (existingData) {
      const hasMore = !!last(existingData)?.cursor;
      const itemIds = existingData.flatMap((page) => pluck("id", page.items as any[]));
      sendToObserver({ data: { itemIds, hasMore } }, observer);
    } else {
      // If we don't have data then we ask the backend for it
      // (only one request will be sent, no matter how many subscribe)
      if (!this.liveListsRequests[key]) {
        //start the request
        this.liveListsRequests[key] = this.listRepo
          .fetchLiveList(listKey)
          .then((itemsPage) => {
            // Put the received data in our cache
            this.liveLists.set(key, [itemsPage]);

            // Put the received items in items-cache (we assume we receive full information about the items)
            const items: any[] = itemsPage.items;
            if (items) {
              items.forEach((item) => {
                // this assumes item-id is the cache key - kinda makes the whole Id datatype pointless
                this.setItemAndNotify(item.id, item);
              });
            }

            const subscribers = this.liveListSubscribers.get(key);
            if (subscribers) {
              const itemIds = pluck("id", itemsPage.items);
              try {
                const send = sender({ data: { itemIds, hasMore: !!itemsPage.cursor } });
                subscribers.forEach(send);
              } catch (error) {
                console.error(error);
              }
            }
          })
          .catch((error) => {
            console.error(error);
            const send = sender({ error });
            const subscribers = this.liveListSubscribers.get(key);
            if (subscribers) {
              subscribers.forEach(send);
            }
          })
          .finally(() => {
            delete this.liveListsRequests[key];
          });
      }
    }

    return () => {
      const subscribers = this.liveListSubscribers.get(key);
      if (subscribers) {
        subscribers.delete(observer);
        if (subscribers.size === 0) {
          this.liveListSubscribers.delete(key);
          // todo: we can abort pending fetches for this list
        }
      }
    };
  }

  async loadMore(listKey: ListKey) {
    const key = listKey.cacheKey();
    if (!key || !this.liveLists.has(key)) return;

    // Get the last page of this live list. It should have the cursor used to fetch the next page
    const lastPage = last(this.liveLists.get(key)!);
    if (lastPage?.cursor) {
      // Get an existing request for the next page, or create one.
      let request = this.liveListsRequests[key];
      if (!request) {
        this.liveListsRequests[key] = request = this.listRepo.fetchLiveListNextPage(listKey, lastPage.cursor);
      }
      const result = await request;
      delete this.liveListsRequests[key];
      // Store the data we received
      if (!this.liveLists.has(key)) {
        return; // the list is gone??
      }
      this.liveLists.get(key)!.push(result);
      // Put the received items in cache (we assume we receive full information about the items)
      const items: any[] = result.items;
      if (items) {
        items.forEach((item) => {
          // this assumes item-id is the cache key - kinda makes the whole Id datatype pointless
          this.setItemAndNotify(item.id, item);
        });
      }
      // notify subscribers of this list that there is more data
      const subscribers = this.liveListSubscribers.get(key);
      if (subscribers) {
        const hasMore = !!result.cursor;
        const itemIds = this.liveLists.get(key)!.flatMap((page) => pluck("id", page.items as any[]));
        const send = sender({ data: { itemIds, hasMore } });
        // todo: schedule for later, don't do it here
        subscribers.forEach(send);
      }
      return result;
    }
  }

  subscribeToAnyItemChange(onChange: (itemId: string, item: Item) => void) {
    this.anyItemChangeSubscribers.add(onChange);
    return () => {
      this.anyItemChangeSubscribers.delete(onChange);
    };
  }

  // --- Batch notifications ---
  private pendingNotifications = new Set<string>();
  private notificationTimer: ReturnType<typeof setTimeout> | null = null;

  private scheduleNotifications(itemId: string) {
    this.pendingNotifications.add(itemId);

    if (!this.notificationTimer) {
      this.notificationTimer = setTimeout(() => {
        this.flushNotifications();
      }, 20);
    }
  }

  private flushNotifications() {
    const itemIds = Array.from(this.pendingNotifications);
    this.pendingNotifications.clear();
    this.notificationTimer = null;

    // learned this trick from Reflect
    unstable_batchedUpdates(() => {
      itemIds.forEach((id) => this.notifyItemSubscribers(id));
    });
  }
}
